/**
 * Super Admin Support Access — client session store.
 *
 * IMPORTANT (production safety):
 *  - Stored in sessionStorage ONLY (per tab, never persisted across browser restarts).
 *  - Completely inert unless a Super Admin explicitly starts a session.
 *  - Never touches the auth session, tokens or the Super Admin's own
 *    `selected_company_id`, so exiting restores their own company instantly.
 */

export type SupportMode = "read_only" | "editing";

export interface SupportSessionState {
  /** support_sessions row id */
  id: string;
  companyId: string;
  companyName: string;
  mode: SupportMode;
  /** ISO timestamp */
  startedAt: string;
}

const STORAGE_KEY = "tm_support_session";

let cached: SupportSessionState | null | undefined;
const listeners = new Set<(s: SupportSessionState | null) => void>();

function read(): SupportSessionState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SupportSessionState;
    if (!parsed?.id || !parsed?.companyId) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function getSupportSession(): SupportSessionState | null {
  if (cached === undefined) cached = read();
  return cached;
}

export function getSupportCompanyId(): string | null {
  return getSupportSession()?.companyId ?? null;
}

export function isSupportReadOnly(): boolean {
  const s = getSupportSession();
  return !!s && s.mode === "read_only";
}

export function setSupportSession(next: SupportSessionState | null) {
  cached = next;
  try {
    if (next) window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    else window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage unavailable — session simply won't persist across reloads */
  }
  listeners.forEach((l) => {
    try {
      l(next);
    } catch {
      /* ignore listener errors */
    }
  });
}

export function subscribeSupportSession(cb: (s: SupportSessionState | null) => void) {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}
